import cls from 'classnames';
import authService from '../../components/api-authorization/AuthorizeService';
import { ValidationError, HandledError } from '../../components/common/forms/ValidationError';
import { FlexColumnStart, toasty } from '../../components/common/forms/FormElements';

/**
 * App HTTP client — a typed extraction of the former `util.fetch`. It wraps the
 * Fetch API and injects the ASP.NET anti-forgery token, the OIDC bearer token,
 * multi-tenant / impersonation headers, and the browser's time zone. Kept in the
 * app (services) layer because it depends on auth, tenant storage, app error
 * types, and toast UI.
 */

/**
 * The browser's current IANA time zone, sent on EVERY request as an RFC 7240
 * preference — `Prefer: timezone=America/Halifax` — so the backend can pair a
 * user-entered moment with the wall clock it was entered on (read server-side by
 * IRequestTimeZone, which falls back to the tenant's TimeZone setting, then the
 * server's zone, when the header is absent). Resolved per
 * request: the zone can change mid-session (laptop travels, OS setting changes).
 */
const browserTimeZone = (): string | null => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
        return null;
    }
};

export type HttpMethod = 'GET' | 'PUT' | 'POST' | 'DELETE';
export type FetchFormat = 'blob' | 'link' | 'json' | 'none';
type HeaderMap = Record<string, string>;

/** Per-request options. `signal` lets a caller abort the underlying fetch (e.g. a superseded grid load). */
export interface RequestOptions {
    signal?: AbortSignal;
}

export const statusCodes = {
    badRequest: 400,
    unauthorized: 401,
    forbidden: 403,
    notFound: 404,
    methodNotAllowed: 405,
    internalServerError: 500,
    notImplemented: 501,
    serviceNotAvailable: 503,
} as const;

export const format = {
    blob: 'blob',
    link: 'link',
    json: 'json',
    none: 'none', // naked fetch, promise response can be manually manipulated
} as const;

export const types = {
    get: 'GET',
    put: 'PUT',
    post: 'POST',
    delete: 'DELETE',
} as const;

/** Core request: builds headers (AAF + bearer + tenant), sends, and handles the response by format. */
export const base = (
    url: string,
    method: HttpMethod,
    data?: unknown,
    responseFormat?: FetchFormat | string,
    headers: HeaderMap | null = null,
    options?: RequestOptions,
): Promise<unknown> => {
    // ASP.NET anti-forgery token inclusion, if present.
    let aafToken: string | null = null;
    const tokenElms = document.getElementsByName('__RequestVerificationToken') ?? [];
    if (tokenElms.length) aafToken = (tokenElms[0] as HTMLInputElement).value;

    // Builds the request (headers + body) from scratch for the given bearer token and sends
    // it, so a resend after a token renewal carries the new token and a fresh body.
    const send = (token: string | null): Promise<Response> => {
        const opt: RequestInit & { headers: HeaderMap } = {
            method,
            headers: {
                ...(headers || { 'Content-Type': 'application/json', Accept: 'application/json' }),
            },
        };

        if (options?.signal) opt.signal = options.signal;

        if (aafToken) opt.headers.RequestVerificationToken = aafToken;

        const timeZone = browserTimeZone();
        if (timeZone) opt.headers.Prefer = `timezone=${timeZone}`;

        if (token) {
            opt.headers.Authorization = `Bearer ${token}`;
            const currentTenantId = window.sessionStorage.getItem('currentTenantId');
            if (currentTenantId) {
                opt.headers['X-CurrentTenantId'] = currentTenantId;
            }
            const impersonating = window.sessionStorage.getItem('impersonating');
            if (impersonating) {
                opt.headers['X-Impersonating'] = impersonating;
            }
        }

        if ((method === 'POST' || method === 'PUT' || method === 'DELETE') && data) {
            if (opt.headers['Content-Type'] === 'application/json') {
                opt.body = JSON.stringify(data, (_key, value: unknown) => (value === undefined ? null : value));
            } else if (opt.headers['Content-Type'] === 'multipart/form-data') {
                const formData = new FormData();
                const record = data as Record<string, string | Blob>;
                Object.keys(record).forEach((name) => formData.append(name, record[name]));
                opt.body = formData;
                delete opt.headers['Content-Type'];
            }
        }

        return fetch(url, opt);
    };

    // Sends with the current OIDC token. A 401 on a request that carried a bearer token
    // means the API no longer accepts that token (expired, or the identity server rotated
    // its signing key) while the login session itself is usually still valid, so renew it
    // silently once (concurrent 401s share one renewal inside the auth service) and resend
    // with the fresh token. If the renewal fails, or the resend is rejected again, the
    // response falls through to the normal unauthorized handling — the resend is never
    // itself retried.
    const sendWithRenew = async (): Promise<Response> => {
        const token = await authService.getAccessToken();
        const response = await send(token);
        if (token && response.status === statusCodes.unauthorized) {
            const renewedToken = await authService.renewAccessToken();
            if (renewedToken) return send(renewedToken);
        }
        return response;
    };

    if (responseFormat === format.none) return sendWithRenew();

    // Fetch with redirect / error handling / validation
    return sendWithRenew().then(async (response) => {
        // 400-500
        if (!response.ok) {
            // Server errors
            if (response.status >= 500 && response.status <= 599) {
                if (response.status === 500) {
                    // The body may not be valid JSON (e.g. an HTML error page from the
                    // proxy). Don't let JSON.parse blow up — throw a clean Error instead.
                    let rj = null;
                    try {
                        rj = await response.json();
                    } catch {
                        rj = null;
                    }
                    if (rj?.errorType != null) {
                        rj = {
                            ...rj,
                            error: rj.errorType,
                        };

                        throw new HandledError(rj);
                    }
                    throw new Error(`Server Error (500) at ${url}`);
                }
                throw new Error(`Server Error (${response.status}) at ${url}`);
            } else if (response.status === statusCodes.methodNotAllowed) {
                throw new Error('Server Error (405) Method Not Allowed');
            } else if (response.status === statusCodes.forbidden) {
                throw new Error('Server Error (403) Occurred - Forbidden/Permission Denied');
            } else if (response.status === statusCodes.unauthorized) {
                throw new Error('Server Error (401) Occurred - Unauthorized');
            } else if (response.status === statusCodes.badRequest) {
                let rj = await response.json();

                if (rj?.errorType != null) {
                    rj = {
                        ...rj,
                        error: rj.errorType,
                    };
                }

                if (rj.title === 'One or more validation errors occurred.') {
                    throw new ValidationError(rj.errors);
                } else if (rj.message) {
                    throw new HandledError(rj);
                }
            } else {
                throw new Error(`Server Error Occurred fetching ${url}: ${response.statusText}`);
            }
            return undefined;
        }

        // 200 — Files/blob
        if (responseFormat === format.blob) {
            return response.blob().then((b) => URL.createObjectURL(b));
        }

        if (responseFormat === format.link) {
            return response.blob().then((b) => {
                const dataUri = URL.createObjectURL(b);
                const a = document.createElement('a');
                a.href = dataUri;

                // The content-disposition header should contain the name of the file.
                const disposition = response.headers.get('Content-Disposition');
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const parts = disposition.split(';').map((s) => s.trim());
                    // Match the original precedence: the first RFC 5987 filename* wins;
                    // otherwise fall back to the last plain filename=.
                    let fileName: string | undefined;
                    const starPart = parts.find((p) => p.startsWith('filename*='));
                    if (starPart) {
                        fileName = decodeURIComponent(starPart.split('=')[1].replace(/utf-8''/i, ''));
                    } else {
                        const namePart = parts.filter((p) => p.startsWith('filename=')).pop();
                        if (namePart) {
                            fileName = namePart.split('=')[1].replace(/['"]/g, '');
                        }
                    }
                    if (fileName) {
                        a.download = fileName;
                    }
                }
                return a;
            });
        }

        // Quick json fetch (json format or default)
        let rj = await response.json();

        if (rj?.errorType != null) {
            rj = {
                ...rj,
                error: rj.errorType,
            };
        }

        return rj;
    });
};

export const post = <T = unknown>(url: string, data?: unknown, responseFormat?: FetchFormat | string, options?: RequestOptions): Promise<T> => base(url, 'POST', data, responseFormat, null, options) as Promise<T>;

export const postFile = <T = unknown>(url: string, data?: unknown): Promise<T> => base(url, 'POST', data, format.none, { 'Content-Type': 'multipart/form-data' }) as Promise<T>;

export const get = <T = unknown>(url: string, responseFormat?: FetchFormat | string): Promise<T> => base(url, 'GET', null, responseFormat) as Promise<T>;

export const put = <T = unknown>(url: string, data?: unknown, responseFormat?: FetchFormat | string): Promise<T> => base(url, 'PUT', data, responseFormat) as Promise<T>;

const del = <T = unknown>(url: string, data?: unknown, responseFormat?: FetchFormat | string): Promise<T> => base(url, 'DELETE', data, responseFormat) as Promise<T>;

export const js = <T = unknown>(url: string): Promise<T> => base(url, 'GET', null, format.json) as Promise<T>;

export const downloadFile = async (url: string, data: unknown, fileName: string): Promise<void> => {
    const objectUrl = await post<string>(url, data, format.blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.target = '_blank';
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export const downloadServerFile = async (url: string, data: unknown, target = '_self'): Promise<void> => {
    const a = await post<HTMLAnchorElement>(url, data, format.link);
    a.target = target;
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export const open = async (url: string, target = '_self'): Promise<void> => {
    const a = await get<HTMLAnchorElement>(url, format.link);
    a.target = target;
    document.body.appendChild(a);
    a.click();
    a.remove();
};

type Fetcher = (url: string, data?: unknown, responseFormat?: FetchFormat | string) => Promise<unknown>;

/** OSFA fetch handler with toast-based error display. Returns parsed JSON, false (redirect), or null (error). */
export const andGetResponse = async (
    type: HttpMethod | string,
    url: string,
    data?: unknown,
    errorHeader?: string,
    onFinally?: () => void,
    suppressServerErrorBoilerplate?: boolean,
): Promise<unknown> => {
    if (!type || !url) {
        throw new Error('httpClient.andGetResponse: Missing required arguments.');
    }

    // get is cast to Fetcher to preserve the original behaviour where a third
    // argument is passed regardless of the verb's declared arity.
    const fetchers: Record<string, Fetcher> = {
        [types.post]: post,
        [types.put]: put,
        [types.get]: get as Fetcher,
        [types.delete]: del,
    };

    const fetcher = fetchers[type];
    if (!fetcher) {
        throw new Error('httpClient.andGetResponse: Type not found.');
    }

    try {
        const response = await fetcher(url, data, format.none) as Response;

        const messageClass = suppressServerErrorBoilerplate
            ? []
            : ['pt-2', 'pb-2', 'font-weight-bold'];

        if (response.redirected) {
            window.location.href = response.url;
            return false;
        }
        if (response.ok) {
            return await response.json();
        }
        let message = '';
        try {
            const errResponseData = await response.json();
            message = typeof errResponseData === 'string' ? errResponseData : errResponseData.message;
        } catch (ex) {
            console.warn(ex);
        } finally {
            toasty.error(
                errorHeader ?? 'Error When Processing Request',
                <FlexColumnStart className={undefined}>
                    {!suppressServerErrorBoilerplate && (
                        <span>There was a server error:</span>
                    )}
                    {!!message && (
                        <span className={cls(messageClass)}>
                            {`${message}`}
                        </span>
                    )}
                    {!suppressServerErrorBoilerplate && (
                        <span>
                            Please try your request again or contact support for assistance.
                        </span>
                    )}
                </FlexColumnStart>,
            );
        }
        return undefined;
    } catch (error) {
        toasty.error(
            errorHeader ?? 'Server Error',
            `There was a server error. ${(error as Error)?.message ? `[${(error as Error).message}]` : ''}  Please try your request again or contact support for assistance.`,
        );
        return null;
    } finally {
        if (onFinally) {
            onFinally();
        }
    }
};

/** Aggregate mirroring the former `util.fetch` shape for 1:1 call-site migration. */
export const httpClient = {
    statusCodes,
    format,
    types,
    base,
    post,
    postFile,
    get,
    put,
    delete: del,
    js,
    downloadFile,
    downloadServerFile,
    open,
    andGetResponse,
};
